import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { LanguageProvider } from './i18n';
import DashboardLayout from './components/layout/DashboardLayout';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import InventoryPage from './pages/InventoryPage';
import ProductDetailPage from './pages/ProductDetailPage';
import SalesPage from './pages/SalesPage';
import ServicesPage from './pages/ServicesPage';
import ProcurementPage from './pages/ProcurementPage';
import PayrollPage from './pages/PayrollPage';
import OperationalExecutionPage from './pages/OperationalExecutionPage';
import ReportsPage from './pages/ReportsPage';
import WhatsAppPage from './pages/WhatsAppPage';
import SettingsPage from './pages/SettingsPage';
import CatalogsPage from './pages/CatalogsPage';
import ExpensesPage from './pages/ExpensesPage';
import CashManagementPage from './pages/CashManagementPage';
import ClientsPage from './pages/ClientsPage';
import LicensesPage from './pages/LicensesPage';
// Alertas Unificado - utiliza AlertsCentralPage (fundido AlertsDashboard + AlertsCentralPage)
import AlertsCentralPage from './pages/AlertsCentralPage';
import FinancialAnalysisPage from './pages/FinancialAnalysisPage';
import RiskManagementPage from './pages/RiskManagementPage';
// Novos módulos - Gestão Avançada de Artigos e Documentos Comerciais
import KitsPage from './pages/KitsPage';
import DocumentsPage from './pages/DocumentsPage';
import TemplateBuilderPage from './pages/TemplateBuilderPage';
import ReceivablesPage from './pages/ReceivablesPage';
import StockTransfersPage from './pages/StockTransfersPage';
// Integridade e Anti-Fraude
import IntegrityPage from './pages/IntegrityPage';
// Gestão de Preços e Fornecedores
import PricingPage from './pages/PricingPage';
import SuppliersPage from './pages/SuppliersPage';
// Sobre o Sistema
import AboutPage from './pages/AboutPage';
// Gaming Center
import GamingCenterPage from './pages/GamingCenterPage';
import FeedbackPage from './pages/FeedbackPage';
import AdminCenterPage from './pages/AdminCenterPage';
import AIEngineeringPage from './pages/AIEngineeringPage';
import ProjectPortalPage from './pages/ProjectPortalPage';
// Obras/Projectos - REMOVIDO: Agora está dentro de ServicesPage como módulo 'obras'
import './App.css';

const APP_TITLE = 'CG Business ERP';

const ProtectedRoute = ({ children = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-muted-foreground">A carregar...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!children) {
    return null;
  }

  return children;
};

function App() {
  useEffect(() => {
    document.title = APP_TITLE;
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/project-portal/:token" element={<ProjectPortalPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              {/* PAINEL */}
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="financial-analysis" element={<FinancialAnalysisPage />} />
              <Route path="risk-management" element={<RiskManagementPage />} />
              {/* OPERAÇÕES */}
              <Route path="inventory/products/:productRef" element={<ProductDetailPage />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="kits" element={<KitsPage />} />
              <Route path="sales" element={<SalesPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="receivables" element={<ReceivablesPage />} />
              <Route path="stock-transfers" element={<StockTransfersPage />} />
              <Route path="services" element={<ServicesPage />} />
              {/* projects agora está dentro de services como módulo 'obras' - aceder via /services?module=obras */}
              <Route path="procurement" element={<ProcurementPage />} />
              {/* FINANCEIRO */}
              <Route path="cash" element={<Navigate to="/sales?tab=cash" replace />} />
              <Route path="cash-management" element={<CashManagementPage />} />
              <Route path="expenses" element={<ExpensesPage />} />
              <Route path="payroll" element={<PayrollPage />} />
              <Route path="operational-execution" element={<OperationalExecutionPage />} />
              {/* RELAÇÕES */}
              <Route path="clients" element={<ClientsPage />} />
              <Route path="suppliers" element={<SuppliersPage />} />
              <Route path="licenses" element={<LicensesPage />} />
              <Route path="alerts" element={<AlertsCentralPage />} />
              {/* SISTEMA */}
              <Route path="catalogs" element={<CatalogsPage />} />
              <Route path="admin-center" element={<AdminCenterPage />} />
              <Route path="ai-engineering" element={<AIEngineeringPage />} />
              <Route path="users" element={<Navigate to="/admin-center?tab=users" replace />} />
              <Route path="whatsapp" element={<WhatsAppPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports-module" element={<Navigate to="/reports?tab=operational" replace />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="template-builder" element={<TemplateBuilderPage />} />
              <Route path="integrity" element={<IntegrityPage />} />
              <Route path="user-management" element={<Navigate to="/admin-center?tab=users" replace />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route path="about" element={<AboutPage />} />
              <Route path="gaming-center" element={<GamingCenterPage />} />
              <Route path="feedback" element={<FeedbackPage />} />
              {/* Redirects para URLs legadas */}
              <Route path="papelaria" element={<Navigate to="/services?module=papelaria" replace />} />
              <Route path="gaming" element={<Navigate to="/gaming-center" replace />} />
              <Route path="alerts-central" element={<Navigate to="/alerts" replace />} />
              <Route path="services-dashboard" element={<Navigate to="/dashboard" replace />} />
              <Route path="items" element={<Navigate to="/inventory" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
